import { CustomSource } from "../models/CustomSource";
import { CustomMouseEvent, mapActive } from "../utils/map-utils";
import { GeoJsonPlannedTrajectoryProps } from "../../osaway/models/CustomGeoJsonProperties";
import { OsaNode } from "../../osaway/models/OsaNode";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { drawTrajectoryStyles } from "../../pages/map/views/trajectoryMapStyles";
import { MapEventType } from "mapbox-gl";
import { OsaWay } from "../../osaway/models/OsaWay";
import { RootStore } from "../../shared/stores/RootStore";
import { MapMode } from "../../project/stores/MapStore";
import { LifeCycleStage } from "../../osaway/models/LifeCycleStage";

export class PlannedTrajectoryMapSource {
  map: mapboxgl.Map;
  active = false;
  rootStore: RootStore;
  drawControl: MapboxDraw | undefined = undefined;
  drawListenerRemovers: (() => void)[] = [];
  osaWaySelectionEventRemovers: (() => void)[] = [];

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;

    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(pointLayerConfig);
      this.map.addLayer(directionLayerConfig);
    }
  }

  clear() {
    if (mapActive(this.map)) {
      this.disableWaySelection();
      this.clearDrawControl();

      try {
        this.map.removeLayer(pointLayerConfig.id);
        this.map.removeLayer(directionLayerConfig.id);
        this.map.removeSource(sourceName);
      } catch (e) {
        console.warn("Error while removing layers and sources", e);
      }
    }
  }

  onModeChange(mode: MapMode) {
    if (
      mode === MapMode.HomePage ||
      mode === MapMode.QueryHlaBbox ||
      mode === MapMode.QueryHlaOriginDestination
    ) {
      this.setMapData([]);
      this.active = false;
    } else {
      this.updateMapData();
      this.active = true;
    }

    this.clearDrawControl();
    this.disableWaySelection();

    if (mode === MapMode.ProjectPage) {
      this.enableWaySelection();
    } else if (mode === MapMode.PlanTrajectory) {
      this.configDrawControl();
    }
  }

  onStoreUpdate() {
    if (!this.active) return;

    this.updateMapData();
  }

  private disableWaySelection() {
    this.osaWaySelectionEventRemovers.map((fn) => fn());
    this.osaWaySelectionEventRemovers = [];
  }

  clearDrawControl() {
    if (mapActive(this.map)) {
      if (this.drawControl) {
        this.drawControl.deleteAll();
        this.map.removeControl(this.drawControl);
        this.drawControl = undefined;
      }
      this.drawListenerRemovers.map((fn) => fn());
      this.drawListenerRemovers = [];
    }
  }

  private updateMapData() {
    const { filterStore, osaWayStore } = this.rootStore;

    const selectedOsaWay = osaWayStore.getSelected();

    const plannedTrajectoryFeatures: GeoJSON.Feature[] = [];

    const shouldShowPlannedTrajectory = (osaWay: OsaWay) => {
      if (selectedOsaWay?.lifeCycleStage === LifeCycleStage.Design) {
        return osaWay.plannedTrajectory && osaWay.id !== selectedOsaWay?.id;
      }
      return osaWay.plannedTrajectory && !osaWay?.geoJson;
    };

    filterStore.filteredOsaWays.forEach((osaWay) => {
      if (filterStore.isOsaWayVisible(osaWay)) {
        if (shouldShowPlannedTrajectory(osaWay)) {
          plannedTrajectoryFeatures.push(
            ...osaWay.plannedTrajectory!.features.map((feature) => ({
              ...feature,
              properties: {
                osaWayId: osaWay.id,
              },
            })),
          );
        }
      }
    });

    this.setMapData(plannedTrajectoryFeatures);
  }

  private setMapData(features: GeoJSON.Feature[]) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features,
      });
    }
  }

  private enableWaySelection() {
    const { osaWayStore } = this.rootStore;
    const map = this.map;

    const canvas = map.getCanvasContainer();
    const cursorPointer = () => (canvas.style.cursor = "pointer");
    const cursorDefault = () => (canvas.style.cursor = "");
    map.on("mouseenter", lineLayerName, cursorPointer);
    map.on("mouseleave", lineLayerName, cursorDefault);

    const handleSelectRoute = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";
      const feature = e.features?.[0]!;
      const props = feature.properties as GeoJsonPlannedTrajectoryProps;

      map.once("mouseup", () => {
        canvas.style.cursor = "";
        osaWayStore.select(props.osaWayId);
      });
    };

    map.on("mousedown", lineLayerName, handleSelectRoute);

    this.osaWaySelectionEventRemovers.push(() => {
      map.off("mouseenter", lineLayerName, cursorPointer);
      map.off("mouseleave", lineLayerName, cursorDefault);
      map.off("mousedown", lineLayerName, handleSelectRoute);
    });

    return this;
  }

  configDrawControl() {
    const { osaWayStore, routeDesignStore } = this.rootStore;
    const osaWay = osaWayStore.getSelected()!;

    this.drawControl = new MapboxDraw({
      displayControlsDefault: false,
      styles: drawTrajectoryStyles,
    });

    this.map.addControl(this.drawControl as mapboxgl.IControl);

    routeDesignStore.setNewPlannedTrajectory(osaWay.plannedTrajectory);

    if (osaWay.plannedTrajectory) {
      this.drawControl.add(osaWay.plannedTrajectory);
    }

    const updateArea = () => {
      const geoJson = this.drawControl!.getAll();

      // remove extra properties added by mapbox, like the line id
      const features = geoJson.features.map((feat) => ({
        type: feat.type,
        geometry: feat.geometry,
        properties: {
          osaWayId: osaWay.id,
        },
      }));

      routeDesignStore.setNewPlannedTrajectory(
        features.length ? { ...geoJson, features } : null,
      );
    };

    const onSelectionChangeHandler = () => {
      const ids = this.drawControl?.getSelectedIds() ?? [];
      routeDesignStore.setSelectedFeatureIds(ids);
    };

    this.map.on("draw.create" as MapEventType, updateArea);
    this.map.on("draw.delete" as MapEventType, updateArea);
    this.map.on("draw.update" as MapEventType, updateArea);
    this.map.on(
      "draw.selectionchange" as MapEventType,
      onSelectionChangeHandler,
    );

    this.drawListenerRemovers.push(() => {
      this.map.off("draw.create", updateArea);
      this.map.off("draw.delete", updateArea);
      this.map.off("draw.update", updateArea);
      this.map.off("draw.selectionchange", onSelectionChangeHandler);
    });
  }

  extendBounds(bounds: mapboxgl.LngLatBounds) {
    const { routeDesignStore, osaWayStore, osaNodeStore } = this.rootStore;

    const selectedWay = osaWayStore.getSelected();
    const selectedNode = osaNodeStore.getSelected();

    const source = this.map.getSource(sourceName);

    const geoJson =
      routeDesignStore.newPlannedTrajectory ||
      ((source as any)._data as GeoJSON.FeatureCollection);

    geoJson.features.forEach((feature) => {
      const props = feature.properties as GeoJsonPlannedTrajectoryProps;

      if (shouldIncludeInBounds(props, { selectedNode, selectedWay })) {
        if (feature.geometry.type === "LineString") {
          feature.geometry.coordinates.forEach((position) => {
            bounds.extend([position[0], position[1]]);
          });
        }
      }
    });
  }
}

interface ExtendBoundsOptions {
  selectedWay?: OsaWay;
  selectedNode?: OsaNode;
}
const shouldIncludeInBounds = (
  props: GeoJsonPlannedTrajectoryProps,
  { selectedWay, selectedNode }: ExtendBoundsOptions,
) => {
  if (selectedNode) return false;
  if (selectedWay) return props.osaWayId === selectedWay.id;
  return false;
};

const sourceName = CustomSource.PlannedTrajectory;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const lineLayerName = `${sourceName}-line`;
const pointLayerConfig: mapboxgl.LineLayerSpecification = {
  id: lineLayerName,
  source: sourceName,
  type: "line",
  layout: {
    "line-cap": "round",
    "line-join": "round",
  },
  paint: {
    "line-color": "#00a498",
    "line-width": 6,
  },
};

const directionLayerName = `${sourceName}-direction`;
const directionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: directionLayerName,
  source: sourceName,
  type: "symbol",
  layout: {
    "symbol-placement": "line",
    "symbol-spacing": 30,
    "icon-image": "chevron-icon",
    "icon-rotation-alignment": "map",
    "icon-size": 0.5,
  },
  paint: {
    "icon-color": "#FFFFFF",
  },
};
